(function () {
  if (customElements.get('bs-compat')) return;
  const upstream = 'https://raw.githubusercontent.com/Onesim-LLC/esim-compatible-devices/main/';
  const sourceUrl = 'https://github.com/Onesim-LLC/esim-compatible-devices';
  const sources = new Map();

  function norm(value) {
    return (value || '').toLowerCase().replace(/[^a-z0-9+]+/g, ' ').trim();
  }

  function compact(value) {
    return norm(value).replace(/\+/g, 'plus').replace(/\s+/g, '');
  }

  function key(brand, model) {
    const b = compact(brand);
    const m = compact(model);
    return b + ':' + (m.startsWith(b) ? m.slice(b.length) : m);
  }

  async function request(url, json, remote) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), remote ? 3500 : 5000);
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        cache: remote ? 'no-cache' : 'default'
      });
      if (!response.ok) throw new Error('Device source unavailable');
      return await (json ? response.json() : response.text());
    } finally {
      clearTimeout(timer);
    }
  }

  function validate(data, license, baseLicense) {
    const withoutCopyright = value => value.replace(/Copyright[^\n]*/gi, '').replace(/\s+/g, ' ').trim();
    if (typeof license !== 'string' || !/Copyright[^\n]*Onesim/i.test(license) ||
        withoutCopyright(license) !== withoutCopyright(baseLicense)) throw new Error('Licence changed');
    const validText = value => typeof value === 'string' && value.trim().length > 0 && value.length <= 4000;
    if (!data || !Array.isArray(data.brands) || !data.brands.length || data.brands.length > 500 ||
        typeof data.updatedAt !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(data.updatedAt)) throw new Error('Invalid device list');
    let count = 0;
    for (const brand of data.brands) {
      if (!brand || !validText(brand.name) || !Array.isArray(brand.devices) ||
          !Array.isArray(brand.notes) || !brand.notes.every(validText)) throw new Error('Invalid brand');
      for (const device of brand.devices) {
        if (!device || !validText(device.model) || !['phone', 'tablet'].includes(device.type) ||
            (device.aliases !== undefined && (!Array.isArray(device.aliases) || !device.aliases.every(validText))) ||
            (device.notes !== undefined && (!Array.isArray(device.notes) || !device.notes.every(validText)))) throw new Error('Invalid device');
        count++;
      }
    }
    if (count < 1 || count > 10000) throw new Error('Invalid device count');
  }

  function merge(base, remote, license) {
    const data = JSON.parse(JSON.stringify(base));
    data.brands = Object.assign(Object.create(null), data.brands);
    const entries = new Map();
    data.models.forEach(model => {
      [model.m].concat(model.a || []).forEach(alias => entries.set(key(model.b, alias), model));
    });
    for (const brand of remote.brands) {
      const brandName = Object.keys(data.brands).find(name => compact(name) === compact(brand.name)) || brand.name;
      const existingNotes = data.brands[brandName] || { en: '', de: '' };
      const additions = brand.notes.filter(note => !norm(existingNotes.en).includes(norm(note))).join(' ');
      data.brands[brandName] = {
        en: [existingNotes.en, additions].filter(Boolean).join(' '),
        de: [existingNotes.de, additions].filter(Boolean).join(' ')
      };
      for (const device of brand.devices) {
        const names = [device.model].concat(device.aliases || []);
        const found = names.map(name => entries.get(key(brandName, name))).find(Boolean);
        const notes = (device.notes || []).join(' ');
        const model = found || { b: brandName, m: device.model, en: '', de: '', s: 'onesim' };
        model.a = Array.from(new Set((model.a || []).concat(names))).filter(name => name !== model.m);
        if (notes && !norm(model.en).includes(norm(notes))) {
          model.en = [model.en, notes].filter(Boolean).join(' ');
          model.de = [model.de, notes].filter(Boolean).join(' ');
        }
        if (!found) data.models.push(model);
        [model.m].concat(model.a).forEach(alias => entries.set(key(brandName, alias), model));
      }
    }
    data.upstreamUpdatedAt = remote.updatedAt;
    data.upstreamSource = sourceUrl;
    data.upstreamLicense = license;
    return data;
  }

  function index(data) {
    data.models.forEach(model => {
      const names = [model.m].concat(model.a || []);
      model.n = norm(names.join(' '));
      model.ca = names.map(compact);
      model.bn = norm(model.b);
    });
    return data;
  }

  function load(src) {
    let state = sources.get(src);
    if (!state) {
      state = { data: null, base: null, pending: null, retryAt: 0 };
      sources.set(src, state);
    }
    if (state.pending) return state.pending;
    if (state.data && Date.now() < state.retryAt) return Promise.resolve(state.data);
    state.pending = (async () => {
      if (!state.base) {
        const base = await request(src, true, false);
        if (!base || !Array.isArray(base.models) || !base.models.length || !base.brands || typeof base.license !== 'string') throw new Error('Invalid backup');
        state.base = base;
      }
      try {
        const [remote, license] = await Promise.all([
          request(upstream + 'data/devices.json', true, true),
          request(upstream + 'LICENSE', false, true)
        ]);
        validate(remote, license, state.base.license);
        state.data = index(merge(state.base, remote, license));
        state.retryAt = Date.now() + 300000;
      } catch (_) {
        state.data = state.data || index(JSON.parse(JSON.stringify(state.base)));
        state.retryAt = Date.now() + 60000;
      }
      return state.data;
    })().finally(() => { state.pending = null; });
    return state.pending;
  }

  class BsCompat extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.t = JSON.parse(this.dataset.strings || '{}');
      this.lang = (document.documentElement.lang || 'en').slice(0, 2) === 'de' ? 'de' : 'en';
      this.input = this.querySelector('[data-q]');
      this.list = this.querySelector('[data-suggest]');
      this.out = this.querySelector('[data-result]');
      this.dialog = this.closest('dialog');
      this.sequence = 0;
      if (!this.dialog && !this.dataset.back) {
        const back = new URLSearchParams(window.location.search).get('return');
        if (back && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[\w-]+/i.test(back)) this.dataset.back = back;
      }
      this.querySelector('.bs-compat__form').addEventListener('submit', event => {
        event.preventDefault();
        this.check();
      });
      this.input.addEventListener('input', () => this.suggest());
      this.input.addEventListener('focus', () => this.load().catch(() => {}));
      if (this.dialog) this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }

    load() {
      return load(this.dataset.src).then(data => { this.data = data; return data; });
    }

    find(query, limit) {
      const nq = norm(query), cq = compact(query);
      if (nq.length < 2) return [];
      const tokens = nq.split(' ');
      const hits = this.data.models.filter(model => {
        const hay = model.bn + ' ' + model.n;
        return tokens.every(token => hay.includes(token)) || model.ca.some(name => name.includes(cq) || (compact(model.b) + name).includes(cq));
      });
      return hits.sort((a, b) => Number(this.exact(b, query)) - Number(this.exact(a, query)) || a.m.length - b.m.length).slice(0, limit || 6);
    }

    exact(model, query) {
      const q = compact(query), b = compact(model.b);
      return model.ca.some(name => name === q || b + name === q || (name.startsWith(b) && name.slice(b.length) === q));
    }

    async search(check) {
      const sequence = ++this.sequence;
      const query = this.input.value.trim();
      this.list.replaceChildren();
      this.out.replaceChildren();
      this.out.removeAttribute('aria-busy');
      if (query.length < 2) return;
      this.out.textContent = this.t.loading || '';
      this.out.setAttribute('aria-busy', 'true');
      try {
        await this.load();
        if (sequence !== this.sequence || query !== this.input.value.trim()) return;
        this.out.replaceChildren();
        const hits = this.find(query, 6);
        const exact = hits.find(model => this.exact(model, query));
        if (check && (exact || hits.length === 1)) return this.show(exact || hits[0]);
        if (check && !hits.length) return this.notFound(query);
        hits.forEach(model => {
          const button = this.el('button', 'bs-compat__opt', model.m);
          button.type = 'button';
          button.addEventListener('click', () => {
            this.sequence++;
            this.input.value = model.m;
            this.list.replaceChildren();
            this.show(model);
          });
          this.list.appendChild(button);
        });
      } catch (_) {
        if (sequence === this.sequence) {
          this.out.replaceChildren(this.el('p', null, this.t.unavailable), this.chatButton(query));
        }
      } finally {
        if (sequence === this.sequence) this.out.removeAttribute('aria-busy');
      }
    }

    suggest() { return this.search(false); }
    check() { return this.search(true); }

    el(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    }

    show(model) {
      const box = this.el('div', 'bs-compat__res bs-compat__res--yes');
      box.appendChild(this.el('p', 'bs-compat__res-title', this.t.yes.replace('{model}', model.m)));
      if (model[this.lang]) box.appendChild(this.el('p', null, model[this.lang]));
      const brand = this.data.brands[model.b] || this.data.brands[Object.keys(this.data.brands).find(name => name.toLowerCase() === model.b.toLowerCase())];
      if (brand && brand[this.lang]) box.appendChild(this.el('p', null, brand[this.lang]));
      box.appendChild(this.el('p', null, this.t.unlocked));
      if (this.dialog || this.dataset.back) {
        const button = this.el('button', 'bs-compat__btn', this.dialog ? this.t.continue : this.t.back);
        button.type = 'button';
        button.addEventListener('click', () => this.done());
        box.appendChild(button);
      }
      this.out.replaceChildren(box);
    }

    chatButton(query) {
      const button = this.el('button', 'bs-compat__btn', this.t.chat);
      button.type = 'button';
      button.addEventListener('click', () => this.chat(query));
      return button;
    }

    notFound(query) {
      const box = this.el('div', 'bs-compat__res bs-compat__res--unsure');
      box.appendChild(this.el('p', 'bs-compat__res-title', this.t.not_found.replace('{query}', query)));
      box.appendChild(this.el('p', null, this.t.not_found_help));
      box.appendChild(this.chatButton(query));
      if (this.dataset.sim) box.appendChild(this.el('p', 'bs-compat__small', this.t.sim_alt));
      this.out.replaceChildren(box);
    }

    chat(query) {
      const message = this.t.chat_message.replace('{model}', query);
      if (this.dialog) this.dialog.close();
      if (window.$crisp) {
        window.$crisp.push(['set', 'message:text', [message]]);
        window.$crisp.push(['do', 'chat:show']);
        window.$crisp.push(['do', 'chat:open']);
      } else if (this.dataset.contact) window.location.href = this.dataset.contact;
    }

    done() {
      if (this.dialog) {
        this.dialog.close();
        const target = document.querySelector('.product-options, [name="add"]');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const focus = target.querySelector('input, button, select') || target;
          if (focus.focus) focus.focus({ preventScroll: true });
        }
      } else if (this.dataset.back) window.location.href = this.dataset.back;
    }
  }

  customElements.define('bs-compat', BsCompat);
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-bs-compat-open]');
    if (!button) return;
    const dialog = document.getElementById(button.getAttribute('data-bs-compat-open'));
    if (dialog && dialog.showModal) {
      dialog.showModal();
      const input = dialog.querySelector('[data-q]');
      if (input) input.focus();
    }
  });
})();
